// ============================================================================
// WorkflowChecklistRail — stage-grouped vertical rail for a workflow instance
// ============================================================================
// Renders one card per STAGE (a grouping of workflow states sharing a
// stage_code). Inside each card:
//   ✓  done       — at least one inner state has been entered
//   ●  current    — one inner state is the workflow's current state
//   ○  future     — no inner state has been entered yet
//
// Stages without a `stageCode` (legacy single-state stages from the hook's
// fallback grouping) render as a single sub-step.
//
// Click a stage → onSelectStage(stageCode). The selected stage is highlighted
// independently of the workflow's actual current stage so the user can
// review past or upcoming stages.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ChecklistData, ChecklistStage } from '../hooks/useWorkflowChecklist';

export interface WorkflowChecklistRailProps {
  data: ChecklistData | null;
  loading?: boolean;
  error?: Error | null;
  instanceId?: string | null;
  /** Stage code highlighted in the rail. */
  selectedStageCode?: string | null;
  onSelectStage: (stageCode: string) => void;
  title?: string;
}

const WorkflowChecklistRail: React.FC<WorkflowChecklistRailProps> = ({
  data, loading, error, instanceId, selectedStageCode, onSelectStage, title = 'Progress',
}) => {
  if (loading && !data) {
    return (
      <div style={containerStyle}>
        <RailHeader title={title} />
        <div style={emptyStyle}>Loading workflow…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <RailHeader title={title} />
        <div style={{ ...emptyStyle, color: 'var(--form-border-error, #ef4444)' }}>
          Failed to load workflow
          <div style={{ fontSize: '0.65rem', marginTop: '0.2rem', opacity: 0.85 }}>{error.message}</div>
        </div>
      </div>
    );
  }

  if (!instanceId) {
    return (
      <div style={containerStyle}>
        <RailHeader title={title} />
        <div style={emptyStyle}>No workflow assigned to this case</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={containerStyle}>
        <RailHeader title={title} />
        <div style={emptyStyle}>Workflow data unavailable</div>
      </div>
    );
  }

  if (data.stages.length === 0) {
    return (
      <div style={containerStyle}>
        <RailHeader title={title} subtitle={data.definitionCode} />
        <div style={emptyStyle}>Workflow has no stages defined</div>
      </div>
    );
  }

  const doneStages = data.stages.filter((s) => s.status === 'done' || s.status === 'current').length;

  return (
    <div style={containerStyle}>
      <RailHeader title={title} subtitle={data.definitionCode} />
      <div style={{ padding: '0.15rem 0' }}>
        {data.stages.map((stage) => (
          <StageRow
            key={stage.stageCode}
            stage={stage}
            isSelected={selectedStageCode === stage.stageCode}
            onClick={() => onSelectStage(stage.stageCode)}
          />
        ))}
      </div>
      <div style={footerStyle}>{doneStages} of {data.stages.length} stages</div>
    </div>
  );
};

// ----------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--border-color)',
  marginBottom: '0.35rem',
  paddingBottom: '0.25rem',
};

const emptyStyle: React.CSSProperties = {
  padding: '0.5rem 0.85rem',
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
};

const footerStyle: React.CSSProperties = {
  padding: '0.35rem 0.85rem 0.55rem',
  fontSize: '0.68rem',
  color: 'var(--text-secondary)',
  borderTop: '1px solid var(--border-color)',
};

const RailHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div style={{
    padding: '0.45rem 0.85rem 0.3rem',
    fontSize: '0.7rem', fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }}>
    <div>{title}</div>
    {subtitle && (
      <div style={{
        fontSize: '0.62rem', fontWeight: 400, marginTop: '0.1rem',
        textTransform: 'none', letterSpacing: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {subtitle}
      </div>
    )}
  </div>
);

const StageRow: React.FC<{
  stage: ChecklistStage;
  isSelected: boolean;
  onClick: () => void;
}> = ({ stage, isSelected, onClick }) => {
  const { status } = stage;
  const markerColor =
    status === 'done' ? '#16a34a' :
    status === 'current' ? '#2563eb' :
    'var(--text-secondary)';

  const marker =
    status === 'done'
      ? <IconSystem.check size={12} />
      : status === 'current'
        ? <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#2563eb' }} />
        : <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            border: '1.5px solid currentColor', opacity: 0.6,
          }} />;

  const StageIcon = stage.stageIcon ? (IconSystem as any)[stage.stageIcon] : null;

  return (
    <button
      type="button"
      onClick={onClick}
      title={stage.stageLabel}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        width: '100%', padding: '0.4rem 0.85rem',
        fontSize: '0.82rem',
        fontWeight: isSelected ? 600 : status === 'current' ? 500 : 400,
        color: status === 'future' ? 'var(--text-secondary)' : 'var(--text-primary)',
        border: 'none',
        background: isSelected ? 'var(--bg-primary)' : 'transparent',
        borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent',
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 16, height: 16, color: markerColor, flexShrink: 0,
      }}>
        {marker}
      </span>
      {StageIcon && (
        <span style={{ color: 'var(--text-secondary)', flexShrink: 0, display: 'inline-flex' }}>
          <StageIcon size={13} />
        </span>
      )}
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
      }}>
        {stage.stageLabel}
      </span>
      {stage.totalCount > 1 && (
        <span style={{
          fontSize: '0.6rem', fontWeight: 600,
          padding: '1px 5px', borderRadius: '999px',
          background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
          flexShrink: 0,
        }}>
          {stage.doneCount}/{stage.totalCount}
        </span>
      )}
      {status === 'current' && (
        <span style={{
          fontSize: '0.58rem', fontWeight: 600,
          padding: '1px 5px', borderRadius: '999px',
          background: '#dbeafe', color: '#1e40af',
          flexShrink: 0,
        }}>now</span>
      )}
    </button>
  );
};

export default WorkflowChecklistRail;
